import React, { useMemo, useState } from "react";
import { getSupabaseClient } from "../lib/supabase";

const COUNTRY_CODES = [
  { code: '+54', label: 'Argentina (+54)' },
  { code: '+61', label: 'Australia (+61)' },
  { code: '+591', label: 'Bolivia (+591)' },
  { code: '+55', label: 'Brazil (+55)' },
  { code: '+56', label: 'Chile (+56)' },
  { code: '+57', label: 'Colombia (+57)' },
  { code: '+506', label: 'Costa Rica (+506)' },
  { code: '+53', label: 'Cuba (+53)' },
  { code: '+593', label: 'Ecuador (+593)' },
  { code: '+503', label: 'El Salvador (+503)' },
  { code: '+33', label: 'France (+33)' },
  { code: '+49', label: 'Germany (+49)' },
  { code: '+502', label: 'Guatemala (+502)' },
  { code: '+592', label: 'Guyana (+592)' },
  { code: '+509', label: 'Haiti (+509)' },
  { code: '+504', label: 'Honduras (+504)' },
  { code: '+91', label: 'India (+91)' },
  { code: '+353', label: 'Ireland (+353)' },
  { code: '+39', label: 'Italy (+39)' },
  { code: '+81', label: 'Japan (+81)' },
  { code: '+52', label: 'Mexico (+52)' },
  { code: '+31', label: 'Netherlands (+31)' },
  { code: '+64', label: 'New Zealand (+64)' },
  { code: '+505', label: 'Nicaragua (+505)' },
  { code: '+1', label: 'North America (+1)' },
  { code: '+507', label: 'Panama (+507)' },
  { code: '+595', label: 'Paraguay (+595)' },
  { code: '+51', label: 'Peru (+51)' },
  { code: '+7', label: 'Russia (+7)' },
  { code: '+65', label: 'Singapore (+65)' },
  { code: '+27', label: 'South Africa (+27)' },
  { code: '+82', label: 'South Korea (+82)' },
  { code: '+34', label: 'Spain (+34)' },
  { code: '+597', label: 'Suriname (+597)' },
  { code: '+46', label: 'Sweden (+46)' },
  { code: '+41', label: 'Switzerland (+41)' },
  { code: '+971', label: 'United Arab Emirates (+971)' },
  { code: '+44', label: 'United Kingdom (+44)' },
  { code: '+598', label: 'Uruguay (+598)' },
  { code: '+58', label: 'Venezuela (+58)' },
];

export type GuideProvisionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (details?: { email?: string; tempPassword?: string }) => void;
};

const EXPERIENCE_TYPE_OPTIONS = [
  "1:1 Journeys or Sessions — Private, personalized experiences with dedicated support",
  "Small Group Experiences — Shared healing spaces with multiple participants",
  "Retreat Center Facilitation — Multi-day immersions with structured guidance",
  "Online Journey Support / Sitting — Virtual presence and harm-reduction support",
  "Microdosing Coaching or Support — Gentle, sub-perceptual dosing with guidance",
  "Medication Tapering Support — In collaboration with licensed providers",
  "Preparation & Integration Only — No in-person dosing, focused on readiness and processing",
  "Family / Couples Containers — Facilitated journeys for relational healing",
  "Other",
];

const SUPPORT_TYPE_OPTIONS = [
  "Microdose Support — Gentle, sub-perceptual dosing with guidance, structure, and harm reduction",
  "Macrodose Support — Full, immersive psychedelic experiences with trained facilitation",
  "Preparation Support — Help getting ready for a journey — intentions, mindset, safety planning",
  "Integration Support — Support after an experience to process insights and apply them",
  "Other",
];

const TRAVEL_WILLINGNESS_OPTIONS = [
  "Local / Regional — I'm available for seekers in my local area or region",
  "Within State/Province — I can travel within my state or province",
  "Nationally — I can travel to other states or provinces",
  "Internationally — I can travel abroad for the right clients",
  "Seekers Travel to Me — People typically travel to my location",
  "Virtual / Online — I work remotely via video",
];

const SERVICE_LOCATION_OPTIONS = [
  "At My Home — Sessions at my personal residence",
  "Office or Professional Space — In a dedicated professional setting",
  "At the Client's Home — In-home sessions at the seeker's location",
  "Online / Virtual — Remote support via video",
  "At Retreat Centers — At dedicated retreat facilities",
  "Church, Non-Profit, or Spiritual Community — Community-based settings",
  "Other",
];

const FOCUS_AREA_GROUPS: Record<string, string[]> = {
  "Personal Growth & Exploration": [
    "Personal Development",
    "Empowerment",
    "Creative Expression",
    "Spiritual Exploration",
    "Perspective Shifting",
  ],
  "Mental & Emotional Wellbeing": [
    "Anxiety",
    "Depression",
    "Mood Disorders",
    "ADD / ADHD",
    "OCD",
    "Autism",
    "Bipolar",
    "Eating Disorders",
    "General Mental Health Support",
  ],
  "Trauma & Recovery": [
    "PTSD",
    "Early Childhood Trauma",
    "Sexual Trauma",
    "Relationship Trauma",
    "Religious Trauma",
    "Racial Trauma",
    "Physical Trauma",
    "Cult / High-Control Group Recovery",
    "Shame",
    "Inner Child Work",
  ],
  "Addiction & Behavioral Patterns": [
    "Addiction / Substance Use",
    "Anger Management",
    "Pain Management",
  ],
  "Relationships & Family": [
    "Couples Support",
    "Divorce & Separation",
    "Healthy Relationship Skills",
    "Interpersonal Relationships",
    "Parenting / Children",
  ],
  "Life Transitions & Identity": [
    "Birth & Postpartum Support",
    "Career Support",
    "Life Transitions",
    "Grief & Loss",
    "Gender Identity",
    "End-of-Life Support",
  ],
  "Other": ["Other"],
};

const FOCUS_AREA_OPTIONS = Object.values(FOCUS_AREA_GROUPS).flat();

const SPECIAL_GROUP_OPTIONS = [
  "BIPOC",
  "LGBTQ+",
  "Women",
  "Men",
  "Couples",
  "Families",
  "Veterans",
  "Seniors",
  "Complex PTSD",
  "Other",
  "None",
];

const RELIGIOUS_BELIEF_OPTIONS = [
  "Agnostic",
  "Atheist",
  "New Age / Spiritual",
  "Christian (Non-Denominational)",
  "Catholic",
  "Mormon / Latter-day Saint",
  "Jewish",
  "Muslim",
  "Buddhist",
  "Hindu",
  "Other",
  "None",
];

const YEARS_PRACTICING_OPTIONS = ["Less than 2 years", "2-5 years", "More than 5 years"];
const CEREMONIES_COUNT_OPTIONS = ["Less than 5", "5-15", "15-50", "50-100", "More than 100"];
const EMPLOYMENT_TYPE_OPTIONS = ["Full time", "Part time"];

const FOCUS_AREA_CATEGORIES = Object.keys(FOCUS_AREA_GROUPS);
const MAX_FOCUS_CATEGORIES = 4;
const MAX_FOCUS_AREAS = 10;

type MultiSelectField =
  | "experienceTypes"
  | "supportTypes"
  | "travelWillingness"
  | "serviceLocations"
  | "specialGroups"
  | "religiousBeliefs";

type OtherFieldKey =
  | "experienceTypes"
  | "supportTypes"
  | "serviceLocations"
  | "focusAreas"
  | "excludedAreas"
  | "specialGroups"
  | "religiousBeliefs";

const OTHER_FIELD_KEYS: OtherFieldKey[] = [
  "experienceTypes",
  "supportTypes",
  "serviceLocations",
  "focusAreas",
  "excludedAreas",
  "specialGroups",
  "religiousBeliefs",
];

const INITIAL_OTHER_VALUES: Record<OtherFieldKey, string> = {
  experienceTypes: "",
  supportTypes: "",
  serviceLocations: "",
  focusAreas: "",
  excludedAreas: "",
  specialGroups: "",
  religiousBeliefs: "",
};

type FormState = {
  fullName: string;
  email: string;
  password: string;
  countryCode: string;
  phone: string;
  location: string;
  bio: string;
  about: string;
  imageUrl: string;
  yearsPracticing: string;
  ceremoniesCount: string;
  employmentType: string;
  modality: string;
  isMainIncome: "yes" | "no";
  hasMedicalDirector: "yes" | "no";
  worksWithOtherFacilitators: "yes" | "no";
  experienceTypes: string[];
  supportTypes: string[];
  travelWillingness: string[];
  serviceLocations: string[];
  focusAreas: string[];
  excludedAreas: string[];
  worksWithMedications: string;
  specialGroups: string[];
  religiousBeliefs: string[];
  legalStatus: string;
  guideStatus: "pending" | "pending_activation" | "consult_required" | "approved" | "active" | "not_fit";
};

const INITIAL_FORM: FormState = {
  fullName: "",
  email: "",
  password: "",
  countryCode: "+1",
  phone: "",
  location: "",
  bio: "",
  about: "",
  imageUrl: "",
  yearsPracticing: "",
  ceremoniesCount: "",
  employmentType: "",
  modality: "",
  isMainIncome: "yes",
  hasMedicalDirector: "no",
  worksWithOtherFacilitators: "yes",
  experienceTypes: [],
  supportTypes: [],
  travelWillingness: [],
  serviceLocations: [],
  focusAreas: [],
  excludedAreas: [],
  worksWithMedications: "",
  specialGroups: [],
  religiousBeliefs: [],
  legalStatus: "",
  guideStatus: "approved",
};

const GuideProvisionModal: React.FC<GuideProvisionModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [selectedFocusCategories, setSelectedFocusCategories] = useState<string[]>([]);
  const [focusSelectionError, setFocusSelectionError] = useState<string | null>(null);
  const [otherValues, setOtherValues] = useState<Record<OtherFieldKey, string>>(INITIAL_OTHER_VALUES);

  const supabase = useMemo(() => getSupabaseClient(), []);

  if (!isOpen) return null;

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isOtherEnabledField = (field: MultiSelectField | OtherFieldKey): field is OtherFieldKey =>
    OTHER_FIELD_KEYS.includes(field as OtherFieldKey);

  const clearOtherValueIfNeeded = (field: OtherFieldKey, values: string[]) => {
    if (!values.includes("Other")) {
      setOtherValues((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
    }
  };

  const handleMultiSelect = (field: MultiSelectField, values: string[]) => {
    setForm((prev) => ({ ...prev, [field]: values }));
    if (isOtherEnabledField(field)) {
      clearOtherValueIfNeeded(field, values);
    }
  };

  const handleOtherValueChange = (field: OtherFieldKey, value: string) => {
    setOtherValues((prev) => ({ ...prev, [field]: value }));
  };

  const toggleFocusCategory = (category: string) => {
    setSelectedFocusCategories((prev) => {
      if (prev.includes(category)) {
        setForm((current) => {
          const options = FOCUS_AREA_GROUPS[category] || [];
          const nextFocusAreas = current.focusAreas.filter((area) => !options.includes(area));
          const nextExcludedAreas = current.excludedAreas.filter((area) => !options.includes(area));
          clearOtherValueIfNeeded("focusAreas", nextFocusAreas);
          clearOtherValueIfNeeded("excludedAreas", nextExcludedAreas);
          return { ...current, focusAreas: nextFocusAreas, excludedAreas: nextExcludedAreas };
        });
        setFocusSelectionError(null);
        return prev.filter((item) => item !== category);
      }
      if (prev.length >= MAX_FOCUS_CATEGORIES) {
        setFocusSelectionError(`You can select up to ${MAX_FOCUS_CATEGORIES} categories. Remove one to add another.`);
        return prev;
      }
      setFocusSelectionError(null);
      return [...prev, category];
    });
  };

  const toggleFocusArea = (category: string, option: string) => {
    if (!selectedFocusCategories.includes(category)) {
      return;
    }
    setForm((prev) => {
      const inFocus = prev.focusAreas.includes(option);
      if (inFocus) {
        const nextFocus = prev.focusAreas.filter((item) => item !== option);
        clearOtherValueIfNeeded("focusAreas", nextFocus);
        setFocusSelectionError(null);
        return { ...prev, focusAreas: nextFocus };
      }
      if (prev.focusAreas.length >= MAX_FOCUS_AREAS) {
        setFocusSelectionError(`You can select up to ${MAX_FOCUS_AREAS} focus areas.`);
        return prev;
      }
      setFocusSelectionError(null);
      return { ...prev, focusAreas: [...prev.focusAreas, option] };
    });
  };

  const toggleExcludedArea = (option: string) => {
    setForm((prev) => {
      const isExcluded = prev.excludedAreas.includes(option);
      const nextExcluded = isExcluded
        ? prev.excludedAreas.filter((item) => item !== option)
        : [...prev.excludedAreas, option];
      const nextFocus = isExcluded ? prev.focusAreas : prev.focusAreas.filter((area) => area !== option);
      clearOtherValueIfNeeded("excludedAreas", nextExcluded);
      clearOtherValueIfNeeded("focusAreas", nextFocus);
      return { ...prev, excludedAreas: nextExcluded, focusAreas: nextFocus };
    });
  };

  const resolveValuesWithOther = (field: OtherFieldKey, values: string[]) => {
    const otherValue = otherValues[field]?.trim();
    return values
      .map((value) => (value === "Other" && otherValue ? otherValue : value))
      .filter((value) => Boolean(value && value.trim().length > 0));
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setMessage(null);

    try {
      const resolvedExperienceTypes = resolveValuesWithOther("experienceTypes", form.experienceTypes);
      const resolvedSupportTypes = resolveValuesWithOther("supportTypes", form.supportTypes);
      const resolvedServiceLocations = resolveValuesWithOther("serviceLocations", form.serviceLocations);
      const resolvedFocusAreas = resolveValuesWithOther("focusAreas", form.focusAreas);
      const resolvedExcludedAreas = resolveValuesWithOther("excludedAreas", form.excludedAreas);
      const resolvedSpecialGroups = resolveValuesWithOther("specialGroups", form.specialGroups);
      const resolvedReligiousBeliefs = resolveValuesWithOther("religiousBeliefs", form.religiousBeliefs);

      const sanitizedPhone = form.phone.replace(/\D/g, '');
      const fullPhone = sanitizedPhone ? `${form.countryCode}${sanitizedPhone}` : null;

      const payload = {
        user: {
          email: form.email.trim(),
          password: form.password.trim() || undefined,
          full_name: form.fullName.trim(),
          phone: fullPhone,
        },
        basic_info: {
          location: form.location.trim(),
          bio: form.bio.trim(),
        },
        professional: {
          years_practicing: form.yearsPracticing.trim() || null,
          ceremonies_count: form.ceremoniesCount.trim() || null,
          employment_type: form.employmentType.trim() || null,
          modality: form.modality.trim() || null,
          is_main_income: form.isMainIncome,
          has_medical_director: form.hasMedicalDirector,
          works_with_other_facilitators: form.worksWithOtherFacilitators,
        },
        professional_experience: {
          experience_types: resolvedExperienceTypes,
          legal_status: form.legalStatus.trim() || null,
          support_types: resolvedSupportTypes,
          travel_willingness: form.travelWillingness,
          service_locations: resolvedServiceLocations,
          focus_categories: selectedFocusCategories,
          focus_areas: resolvedFocusAreas,
          excluded_areas: resolvedExcludedAreas,
          works_with_medications: form.worksWithMedications.trim() || null,
          special_groups: resolvedSpecialGroups,
          religious_beliefs: resolvedReligiousBeliefs,
        },
        enhanced_profile: {
          about: form.about.trim() || null,
          image_url: form.imageUrl.trim() || null,
        },
        guide_status: form.guideStatus,
        places: resolvedServiceLocations,
      };

      const { data, error } = await supabase.functions.invoke<{
        success?: boolean;
        error?: string;
        details?: unknown;
        credentials?: { email: string; password: string };
      }>("create-guide-account", {
        body: payload,
      });

      if (error) {
        // Handle network/invocation errors
        console.error("Edge function invocation error:", error);
        // Try to extract more details from the error
        const errorDetails = typeof error === 'object' && error !== null
          ? JSON.stringify(error, null, 2)
          : String(error);
        throw new Error(`Edge function error: ${error.message || errorDetails}`);
      }

      if (!data) {
        throw new Error("No response from edge function");
      }

      if (!data.success) {
        // Extract detailed error message from response
        console.error("Edge function returned error:", data);
        let errorMessage = data.error || "Unable to create guide account";
        // Include details if available
        if (data.details) {
          const detailsStr = typeof data.details === 'string'
            ? data.details
            : JSON.stringify(data.details);
          errorMessage = `${errorMessage}: ${detailsStr}`;
        }
        throw new Error(errorMessage);
      }

      onSuccess?.(
        data.credentials
          ? { email: data.credentials.email, tempPassword: data.credentials.password }
          : undefined
      );
      resetFormState();
    } catch (err) {
      const text = err instanceof Error ? err.message : "Unexpected error";
      setMessage({ type: "error", text });
    } finally {
      setSubmitting(false);
    }
  };

  const resetFormState = () => {
    setForm(INITIAL_FORM);
    setSelectedFocusCategories([]);
    setFocusSelectionError(null);
    setOtherValues(INITIAL_OTHER_VALUES);
  };

  const closeModal = () => {
    resetFormState();
    setMessage(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-10">
      <div className="max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-[#2C2731] bg-[#FFFCEF] shadow-[0_45px_90px_-40px_rgba(32,24,44,0.65)]">
        <header className="flex items-center justify-between border-b border-[#2C2731]/10 px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#707C8B]">New guide</p>
            <h2 className="font-recoleta text-2xl text-[#2C2731]">Provision account</h2>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="rounded-full border border-[#2C2731]/30 px-4 py-1.5 text-sm font-semibold text-[#2C2731] transition hover:bg-[#E8DFBD]"
          >
            Close
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
          {message && (
            <div
              className={`rounded-2xl border px-4 py-3 text-sm ${
                message.type === "success"
                  ? "border-[#214D3C]/30 bg-[#E4F1E8] text-[#214D3C]"
                  : "border-[#B24E4E]/30 bg-[#FCEDEA] text-[#8C2F2F]"
              }`}
            >
              {message.text}
            </div>
          )}

          <section className="space-y-4 rounded-2xl border border-[#2C2731]/15 bg-white p-4">
            <h3 className="text-lg font-semibold text-[#2C2731]">Account</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-[#2C2731]">
                Full name
                <input
                  value={form.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  required
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                />
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                />
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Phone
                <div className="mt-1 flex gap-2">
                  <select
                    value={form.countryCode}
                    onChange={(e) => handleChange("countryCode", e.target.value)}
                    className="w-40 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  <input
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="555 123 4567"
                    className="flex-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  />
                </div>
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Temporary password (optional)
                <input
                  value={form.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  placeholder="Leave blank to auto-generate"
                />
              </label>
            </div>
          </section>

          <section className="space-y-4 rounded-2xl border border-[#2C2731]/15 bg-white p-4">
            <h3 className="text-lg font-semibold text-[#2C2731]">Story & basics</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-[#2C2731]">
                Location
                <input
                  value={form.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  required
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                />
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Guide status
                <select
                  value={form.guideStatus}
                  onChange={(e) => handleChange("guideStatus", e.target.value as FormState["guideStatus"])}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="pending">Pending</option>
                  <option value="pending_activation">Pending Activation</option>
                  <option value="consult_required">Consult Required</option>
                  <option value="approved">Approved</option>
                  <option value="active">Active</option>
                  <option value="not_fit">Not a Fit</option>
                </select>
              </label>
            </div>
            <label className="flex flex-col text-sm text-[#2C2731]">
              Short bio
              <textarea
                rows={3}
                value={form.bio}
                onChange={(e) => handleChange("bio", e.target.value)}
                required
                className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
              />
            </label>
          </section>

          <section className="space-y-4 rounded-2xl border border-[#2C2731]/15 bg-white p-4">
            <h3 className="text-lg font-semibold text-[#2C2731]">Professional snapshot</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-[#2C2731]">
                Years practicing
                <select
                  value={form.yearsPracticing}
                  onChange={(e) => handleChange("yearsPracticing", e.target.value)}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="">Select</option>
                  {YEARS_PRACTICING_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Ceremonies facilitated
                <select
                  value={form.ceremoniesCount}
                  onChange={(e) => handleChange("ceremoniesCount", e.target.value)}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="">Select</option>
                  {CEREMONIES_COUNT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Employment type
                <select
                  value={form.employmentType}
                  onChange={(e) => handleChange("employmentType", e.target.value)}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="">Select</option>
                  {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <label className="flex flex-col text-sm text-[#2C2731]">
                Main income?
                <select
                  value={form.isMainIncome}
                  onChange={(e) => handleChange("isMainIncome", e.target.value as FormState["isMainIncome"])}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Medical director?
                <select
                  value={form.hasMedicalDirector}
                  onChange={(e) => handleChange("hasMedicalDirector", e.target.value as FormState["hasMedicalDirector"])}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Works with other facilitators?
                <select
                  value={form.worksWithOtherFacilitators}
                  onChange={(e) =>
                    handleChange("worksWithOtherFacilitators", e.target.value as FormState["worksWithOtherFacilitators"])
                  }
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
            </div>
          </section>

          <section className="space-y-4 rounded-2xl border border-[#2C2731]/15 bg-white p-4">
            <h3 className="text-lg font-semibold text-[#2C2731]">Practice details</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-[#2C2731]">
                Experience types
                <select
                  multiple
                  value={form.experienceTypes}
                  onChange={(e) =>
                    handleMultiSelect(
                      "experienceTypes",
                      Array.from(e.target.selectedOptions).map((opt) => opt.value)
                    )
                  }
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2 h-32"
                >
                  {EXPERIENCE_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {form.experienceTypes.includes("Other") && (
                  <input
                    value={otherValues.experienceTypes}
                    onChange={(e) => handleOtherValueChange("experienceTypes", e.target.value)}
                    placeholder="Describe other experience type"
                    className="mt-2 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  />
                )}
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Support types
                <select
                  multiple
                  value={form.supportTypes}
                  onChange={(e) =>
                    handleMultiSelect(
                      "supportTypes",
                      Array.from(e.target.selectedOptions).map((opt) => opt.value)
                    )
                  }
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2 h-32"
                >
                  {SUPPORT_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {form.supportTypes.includes("Other") && (
                  <input
                    value={otherValues.supportTypes}
                    onChange={(e) => handleOtherValueChange("supportTypes", e.target.value)}
                    placeholder="Describe other support type"
                    className="mt-2 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  />
                )}
              </label>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-[#2C2731]">
                Travel willingness
                <select
                  multiple
                  value={form.travelWillingness}
                  onChange={(e) =>
                    handleMultiSelect(
                      "travelWillingness",
                      Array.from(e.target.selectedOptions).map((opt) => opt.value)
                    )
                  }
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2 h-32"
                >
                  {TRAVEL_WILLINGNESS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Service locations
                <select
                  multiple
                  value={form.serviceLocations}
                  onChange={(e) =>
                    handleMultiSelect(
                      "serviceLocations",
                      Array.from(e.target.selectedOptions).map((opt) => opt.value)
                    )
                  }
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2 h-32"
                >
                  {SERVICE_LOCATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {form.serviceLocations.includes("Other") && (
                  <input
                    value={otherValues.serviceLocations}
                    onChange={(e) => handleOtherValueChange("serviceLocations", e.target.value)}
                    placeholder="Describe other service location"
                    className="mt-2 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  />
                )}
              </label>
            </div>
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-[#2C2731]">Focus categories</p>
                  <p className="text-xs text-[#2C2731]/60">
                    Select up to {MAX_FOCUS_CATEGORIES} categories. Currently {selectedFocusCategories.length} selected.
                  </p>
                </div>
                <p className="text-xs text-[#2C2731]/60">
                  Focus areas selected: {form.focusAreas.length}/{MAX_FOCUS_AREAS}
                </p>
              </div>
              {focusSelectionError && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{focusSelectionError}</p>
              )}
              <div className="flex flex-wrap gap-2 rounded-2xl border border-[#2C2731]/15 bg-[#FFFCEF]/60 p-3">
                {FOCUS_AREA_CATEGORIES.map((category) => {
                  const isSelected = selectedFocusCategories.includes(category);
                  return (
                    <button
                      type="button"
                      key={category}
                      onClick={() => toggleFocusCategory(category)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        isSelected
                          ? "border-[#214D3C] bg-[#214D3C] text-[#FEF6D6]"
                          : "border-[#2C2731]/25 text-[#2C2731]"
                      }`}
                    >
                      {category}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-3 rounded-2xl border border-[#2C2731]/15 bg-[#FFFCEF]/40 p-4">
              {selectedFocusCategories.length === 0 ? (
                <p className="text-sm text-[#2C2731]/70">Select at least one category to choose specific focus areas.</p>
              ) : (
                selectedFocusCategories.map((category) => {
                  const options = FOCUS_AREA_GROUPS[category] || [];
                  const selectedCount = form.focusAreas.filter((area) => options.includes(area)).length;
                  return (
                    <div key={category} className="rounded-xl border border-[#2C2731]/10 bg-white px-3 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-semibold text-[#2C2731]">{category}</h4>
                        <span className="text-xs text-[#2C2731]/60">{selectedCount} selected</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {options.map((option) => {
                          const selected = form.focusAreas.includes(option);
                          const excluded = form.excludedAreas.includes(option);
                          return (
                            <button
                              type="button"
                              key={option}
                              className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                                excluded
                                  ? "border-red-300 bg-red-50 text-red-700"
                                  : selected
                                  ? "border-[#214D3C] bg-[#214D3C] text-[#FEF6D6]"
                                  : "border-[#2C2731]/25 text-[#2C2731]"
                              }`}
                              onClick={() => toggleFocusArea(category, option)}
                              disabled={excluded}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
              {form.focusAreas.includes("Other") && (
                <input
                  value={otherValues.focusAreas}
                  onChange={(e) => handleOtherValueChange("focusAreas", e.target.value)}
                  placeholder="Describe other focus areas"
                  className="rounded-lg border border-[#2C2731]/30 bg-white px-3 py-2 text-sm"
                />
              )}
            </div>
            <label className="flex flex-col text-sm text-[#2C2731]">
              Excluded areas
              <div className="mt-2 flex flex-wrap gap-2 rounded-2xl border border-[#2C2731]/15 bg-[#FFFCEF]/60 p-3">
                {FOCUS_AREA_OPTIONS.map((option) => {
                  const excluded = form.excludedAreas.includes(option);
                  return (
                    <button
                      type="button"
                      key={option}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        excluded ? "border-red-400 bg-red-100 text-red-700" : "border-[#2C2731]/25 text-[#2C2731]"
                      }`}
                      onClick={() => toggleExcludedArea(option)}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {form.excludedAreas.includes("Other") && (
                <input
                  value={otherValues.excludedAreas}
                  onChange={(e) => handleOtherValueChange("excludedAreas", e.target.value)}
                  placeholder="Describe other areas to exclude"
                  className="mt-2 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                />
              )}
            </label>
            <div className="grid gap-4 md:grid-cols-3">
              <label className="flex flex-col text-sm text-[#2C2731]">
                Works with medications?
                <select
                  value={form.worksWithMedications}
                  onChange={(e) => handleChange("worksWithMedications", e.target.value)}
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Special groups
                <select
                  multiple
                  value={form.specialGroups}
                  onChange={(e) =>
                    handleMultiSelect(
                      "specialGroups",
                      Array.from(e.target.selectedOptions).map((opt) => opt.value)
                    )
                  }
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2 h-32"
                >
                  {SPECIAL_GROUP_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {form.specialGroups.includes("Other") && (
                  <input
                    value={otherValues.specialGroups}
                    onChange={(e) => handleOtherValueChange("specialGroups", e.target.value)}
                    placeholder="Describe other special groups"
                    className="mt-2 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  />
                )}
              </label>
              <label className="flex flex-col text-sm text-[#2C2731]">
                Religious beliefs
                <select
                  multiple
                  value={form.religiousBeliefs}
                  onChange={(e) =>
                    handleMultiSelect(
                      "religiousBeliefs",
                      Array.from(e.target.selectedOptions).map((opt) => opt.value)
                    )
                  }
                  className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2 h-32"
                >
                  {RELIGIOUS_BELIEF_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {form.religiousBeliefs.includes("Other") && (
                  <input
                    value={otherValues.religiousBeliefs}
                    onChange={(e) => handleOtherValueChange("religiousBeliefs", e.target.value)}
                    placeholder="Describe other beliefs"
                    className="mt-2 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                  />
                )}
              </label>
            </div>
                    <label className="flex flex-col text-sm text-[#2C2731]">
                        Legal status
                        <select
                            value={form.legalStatus}
                            onChange={(e) => handleChange("legalStatus", e.target.value)}
                            className="mt-1 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] px-3 py-2"
                        >
                            <option value="">Select</option>
                            <option value="Government regulated options only — I work only in legal psychedelic modalities where I live">Government regulated options only — I work only in legal psychedelic modalities where I live</option>
                            <option value="Underground practitioner — I work in contexts that may not be formally legal where I live">Underground practitioner — I work in contexts that may not be formally legal where I live</option>
                            <option value="Both — I work in legal contexts and in underground settings">Both — I work in legal contexts and in underground settings</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                    </label>
          </section>

          <div className="flex flex-col-reverse gap-3 border-t border-[#2C2731]/10 pt-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-full border border-[#2C2731]/30 px-5 py-2 text-sm font-semibold text-[#2C2731] transition hover:border-[#2C2731]"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[#2C2731] px-6 py-2 text-sm font-semibold text-[#FEF6D6] transition hover:bg-[#1d1a25] disabled:opacity-60"
            >
              {submitting ? "Creating…" : "Create guide"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuideProvisionModal;
